import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CloudProviderMark from "../../../../components/Common/CloudProviderMark";
import { formatUtcDateSimple } from "../../../../helpers/formatUtcDate";
import {
  ACCOUNT_DETAIL_FONT_FAMILY,
  ACCOUNT_DETAIL_FONT_SX,
} from "../../../../components/AccountDetailPolicy/accountDetailTypography";
import { EditAccountTablePagination } from "../../../../components/AccountDetailPolicy/editAccountTableControls";
import CompareScanPickerDialog from "./CompareScanPickerDialog";
import {
  compareCspmScans,
  compareCspmResources,
  clearCspmCompare,
  getCspmChecks,
} from "../../../../store/cspm/cspmSlice";
import ErrorUI from "../ErrorUI";
import CompareResourcesDrawer from "./CompareResourcesDrawer";
import {
  formatScore,
  getComparableScans,
  getScanTimestamp,
  getSeverityColor,
  getStatusColor,
} from "./compareUtils";
import CompareSummaryCards from "./CompareSummaryCards";

const ACCENT = "#F98125";
const FIELD_HEIGHT = 44;

/** Matches Configuration content cards (Cloud Accounts / Controls). */
const CONFIG_CARD_SX = {
  backgroundColor: "#f3f4f6",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
};

const MENU_ITEM_SX = {
  fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#111827",
  mx: 0.5,
  my: 0.25,
  borderRadius: "6px",
  "&:hover": {
    backgroundColor: "#fff4eb",
  },
  "&.Mui-selected": {
    fontWeight: 600,
    color: "#fff",
    backgroundColor: ACCENT,
    "&:hover": {
      backgroundColor: "#e8741c",
      color: "#fff",
    },
  },
};

const accountSelectSx = {
  minWidth: 220,
  height: FIELD_HEIGHT,
  fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
  fontSize: "0.875rem",
  backgroundColor: "#fff",
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e2e8f0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#cbd5e1",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#111827",
    borderWidth: "1px",
  },
  "& .MuiSelect-select": {
    py: 0,
    display: "flex",
    alignItems: "center",
    height: FIELD_HEIGHT,
    boxSizing: "border-box",
  },
  "& .MuiSelect-icon": {
    color: "#94a3b8",
  },
};

const selectMenuProps = {
  PaperProps: {
    sx: {
      fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
      border: "1px solid #e2e8f0",
      mt: 0.5,
      maxHeight: 300,
      "& .MuiList-root": {
        py: 0.5,
      },
    },
  },
};

const CELL_TEXT = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: "#334155",
  lineHeight: 1.4,
};

const HEADER_CELL_SX = {
  ...CELL_TEXT,
  fontWeight: 600,
  fontSize: "0.6875rem",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#475569",
  py: 1.25,
  px: 1.75,
  textAlign: "center",
  borderBottom: "1px solid #e2e8f0",
  backgroundColor: "#f8fafc",
  whiteSpace: "nowrap",
};

const BODY_CELL_SX = {
  ...CELL_TEXT,
  py: 1.5,
  px: 1.25,
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "middle",
};

/** Title, Baseline (3), Compare (3), and the resource action. */
const COMPARE_COLUMN_COUNT = 8;
const COMPARE_COLUMN_INFO = {
  2: "Percentage of scanned resources that passed this check.",
  5: "Percentage of scanned resources that passed this check.",
};
const GROUP_DIVIDER = "1px solid #cbd5e1";
const TITLE_COL_WIDTH = { width: "16%" };
const GROUP_END_SX = { borderRight: GROUP_DIVIDER };

const GROUP_HEADER_SX = {
  ...HEADER_CELL_SX,
  fontWeight: 700,
  fontSize: "0.75rem",
  letterSpacing: "0.08em",
  color: "#0f172a",
  backgroundColor: "#f1f5f9",
  borderBottom: GROUP_DIVIDER,
  py: 1.25,
};

const ScanGroupHeaderCell = ({ label, date, divider = true }) => (
  <TableCell
    colSpan={3}
    align="center"
    sx={{
      ...GROUP_HEADER_SX,
      ...(divider ? { borderRight: GROUP_DIVIDER } : null),
    }}
  >
    <Box component="span" sx={{ display: "block" }}>{label}</Box>
    {date ? (
      <Box
        component="span"
        sx={{
          display: "block",
          mt: 0.25,
          fontWeight: 500,
          fontSize: "0.625rem",
          letterSpacing: 0,
          textTransform: "none",
          color: "#64748b",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {date}
      </Box>
    ) : null}
  </TableCell>
);

const FILTER_SELECT_SX = {
  minWidth: 140,
  height: 40,
  fontSize: "0.875rem",
  backgroundColor: "#fff",
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#111827",
    borderWidth: "1px",
  },
  "& .MuiSelect-select": {
    py: 0,
    display: "flex",
    alignItems: "center",
    height: 40,
    boxSizing: "border-box",
  },
  "& .MuiSelect-icon": { color: "#94a3b8" },
};

const SEARCH_FIELD_SX = {
  width: { xs: "100%", sm: 220 },
  minWidth: { xs: 0, sm: 220 },
  ml: { sm: "auto" },
  "& .MuiOutlinedInput-root": {
    height: 40,
    fontSize: "0.875rem",
    backgroundColor: "#fff",
    borderRadius: "8px",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": {
      borderColor: "#111827",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    py: 0,
    height: "100%",
    boxSizing: "border-box",
  },
};

const CHANGE_TYPE_CHIP_SX = {
  height: 22,
  fontSize: "0.6875rem",
  fontWeight: 600,
  borderRadius: "6px",
  backgroundColor: "#f1f5f9",
  color: "#475569",
  "& .MuiChip-label": { px: 1 },
};

/** Title-case labels for filter options (values stay UPPER for data matching). */
const toTitleCaseLabel = (value) => {
  const raw = String(value || "");
  if (!raw) return "";
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
};

const listServiceNames = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value && typeof value === "object") {
    return [
      ...(Array.isArray(value.second) ? value.second : []),
      ...(Array.isArray(value.first) ? value.first : []),
    ].filter(Boolean);
  }
  return typeof value === "string" && value ? [value] : [];
};

const servicesOnCheck = (row) => [
  ...listServiceNames(row?.service),
  ...listServiceNames(row?.availableTypes),
  ...listServiceNames(row?.AvailableTypes),
];

const compareScansPayload = (firstRunId, secondRunId, service) => ({
  firstRunId,
  secondRunId,
  ...(service && service !== "all" ? { service } : {}),
});

const ScanSlot = ({ label, value }) => (
  <Box sx={{ minWidth: 0, flex: "0 1 auto" }}>
    <Typography
      sx={{
        fontSize: "0.625rem",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: "#64748b",
        lineHeight: 1.2,
        mb: 0.15,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.8125rem",
        fontWeight: 600,
        color: value ? "#111827" : "#94a3b8",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        lineHeight: 1.3,
      }}
    >
      {value || "Choose scan"}
    </Typography>
  </Box>
);

const ComparativeScan = ({
  accountsWithWorkloads,
  selectValue,
  selectedAccount,
  selectedWorkload,
  selectedProvider,
  onSelectAccount,
  onSelectWorkload,
  cspmResultOrderedMap,
  availableDates = [],
  isGettingCspmChecks,
  isActive = true,
}) => {
  const dispatch = useDispatch();
  const {
    isComparingCspmScans,
    compareCspmScansError,
    cspmCompareResult,
  } = useSelector((state) => state.cspm);

  const [baselineScan, setBaselineScan] = useState(null);
  const [compareScan, setCompareScan] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [serviceOptionsCache, setServiceOptionsCache] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRow, setExpandedRow] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [selectedResourceTab, setSelectedResourceTab] = useState(null);
  const autoCompareKeyRef = useRef(null);
  const knownScansRef = useRef([]);
  const attemptedDatesRef = useRef(new Set());

  const selectedWorkloadName = useMemo(() => {
    if (!selectedWorkload) return undefined;
    for (const acc of accountsWithWorkloads) {
      const workload = acc.schedules?.find((item) => item.scheduleId === selectedWorkload);
      if (workload) return workload.scheduleName;
    }
    return undefined;
  }, [accountsWithWorkloads, selectedWorkload]);

  useEffect(() => {
    setBaselineScan(null);
    setCompareScan(null);
    setPickerOpen(false);
    setSearch("");
    setSeverityFilter("all");
    setStatusFilter("all");
    setServiceFilter("all");
    setServiceOptionsCache([]);
    setPage(0);
    setExpandedRow(null);
    setDrawerOpen(false);
    setSelectedCheck(null);
    setSelectedResourceTab(null);
    autoCompareKeyRef.current = null;
    knownScansRef.current = [];
    attemptedDatesRef.current = new Set();
    dispatch(clearCspmCompare());
  }, [selectedAccount, selectedWorkload, dispatch]);

  // Default: compare the last two completed scans for this account/workload.
  useEffect(() => {
    if (!isActive || !selectedAccount || isGettingCspmChecks || pickerOpen) return;

    const key = `${selectedAccount}::${selectedWorkload || ""}`;
    if (autoCompareKeyRef.current === key) return;

    const applyDefaultPair = (newer, older) => {
      if (!newer?.runId || !older?.runId || newer.runId === older.runId) return false;
      const baseline =
        getScanTimestamp(older) <= getScanTimestamp(newer) ? older : newer;
      const compare = baseline.runId === older.runId ? newer : older;
      setBaselineScan(baseline);
      setCompareScan(compare);
      autoCompareKeyRef.current = key;
      dispatch(compareCspmScans(compareScansPayload(baseline.runId, compare.runId, "all")));
      return true;
    };

    // Parent date fetches replace the map — accumulate complete scans across loads.
    const fromMap = getComparableScans(cspmResultOrderedMap, selectedWorkload);
    const byRunId = new Map();
    [...knownScansRef.current, ...fromMap].forEach((scan) => {
      if (scan?.runId) byRunId.set(scan.runId, scan);
    });
    const merged = [...byRunId.values()].sort(
      (a, b) => getScanTimestamp(b) - getScanTimestamp(a)
    );
    knownScansRef.current = merged;

    if (merged.length >= 2) {
      applyDefaultPair(merged[0], merged[1]);
      return;
    }

    const sortedDates = [...(availableDates || [])].sort(
      (a, b) =>
        new Date(String(b).replace(" ", "T")) - new Date(String(a).replace(" ", "T"))
    );
    if (sortedDates.length === 0) return;

    const nextDate = sortedDates.find((dt) => {
      const token = String(dt);
      if (attemptedDatesRef.current.has(token)) return false;
      return true;
    });

    if (!nextDate) {
      autoCompareKeyRef.current = key;
      return;
    }

    attemptedDatesRef.current.add(String(nextDate));
    dispatch(
      getCspmChecks({
        accountNumber: selectedAccount,
        date: nextDate,
        ...(selectedWorkloadName ? { workloadScheduleName: selectedWorkloadName } : {}),
      })
    );
  }, [
    isActive,
    selectedAccount,
    selectedWorkload,
    selectedWorkloadName,
    cspmResultOrderedMap,
    availableDates,
    isGettingCspmChecks,
    pickerOpen,
    dispatch,
  ]);

  const selectedDisplay = useMemo(() => {
    if (!selectValue) return "";
    if (selectedWorkload) {
      for (const acc of accountsWithWorkloads) {
        const workload = acc.schedules?.find((s) => s.scheduleId === selectedWorkload);
        if (workload) return workload.scheduleName;
      }
      return selectedWorkload;
    }
    const acc = accountsWithWorkloads.find((a) => a.accountNumber === selectedAccount);
    return acc ? `${acc.accountName} (${acc.accountNumber})` : selectValue;
  }, [selectValue, selectedWorkload, selectedAccount, accountsWithWorkloads]);

  const handleAccountDropdownChange = (event) => {
    const value = event.target.value;
    const isAccount = accountsWithWorkloads.some((acc) => acc.accountNumber === value);
    if (isAccount) {
      onSelectAccount(value);
      return;
    }
    const parentAcc = accountsWithWorkloads.find((acc) =>
      acc.schedules?.some((schedule) => schedule.scheduleId === value)
    );
    const workload = parentAcc?.schedules?.find((schedule) => schedule.scheduleId === value);
    if (parentAcc && workload) {
      onSelectWorkload(parentAcc.accountNumber, workload.scheduleId, workload.scheduleName);
    }
  };

  const applyScanPair = (first, second) => {
    if (!first?.runId || !second?.runId || first.runId === second.runId) return;
    setBaselineScan(first);
    setCompareScan(second);
    setPickerOpen(false);
    dispatch(compareCspmScans(compareScansPayload(first.runId, second.runId, serviceFilter)));
  };

  const handleCompare = () => {
    if (!baselineScan?.runId || !compareScan?.runId || baselineScan.runId === compareScan.runId) return;
    dispatch(compareCspmScans(compareScansPayload(baselineScan.runId, compareScan.runId, serviceFilter)));
  };

  const changedChecks = useMemo(
    () => cspmCompareResult?.changedChecks || [],
    [cspmCompareResult]
  );

  const servicesByCheckId = useMemo(() => {
    const map = {};
    Object.values(cspmResultOrderedMap || {}).forEach((scan) => {
      (scan?.auditChecks?.Checks || []).forEach((check) => {
        const id = check.CheckId || check.checkId;
        if (!id) return;
        const types = [
          ...listServiceNames(check.AvailableTypes),
          ...listServiceNames(check.availableTypes),
        ];
        if (!types.length) return;
        map[id] = Array.from(new Set([...(map[id] || []), ...types]));
      });
    });
    return map;
  }, [cspmResultOrderedMap]);

  const resolveCheckServices = (row) =>
    Array.from(
      new Set([
        ...servicesOnCheck(row),
        ...((row?.checkId && servicesByCheckId[row.checkId]) || []),
      ])
    );

  const availableServices = useMemo(() => {
    const names = new Set(serviceOptionsCache);
    changedChecks.forEach((row) => {
      resolveCheckServices(row).forEach((name) => names.add(name));
    });
    return Array.from(names).sort();
  }, [changedChecks, serviceOptionsCache, servicesByCheckId]);

  useEffect(() => {
    if (!changedChecks.length) return;
    setServiceOptionsCache((prev) => {
      const names = new Set(prev);
      changedChecks.forEach((row) => {
        resolveCheckServices(row).forEach((name) => names.add(name));
      });
      const next = Array.from(names).sort();
      if (next.length === prev.length && next.every((value, index) => value === prev[index])) {
        return prev;
      }
      return next;
    });
  }, [changedChecks, servicesByCheckId]);

  const filteredChecks = useMemo(() => {
    let rows = changedChecks;
    if (severityFilter !== "all") {
      rows = rows.filter(
        (row) => (row.severity?.second || row.severity?.first || "").toUpperCase() === severityFilter
      );
    }
    if (statusFilter === "changed") {
      rows = rows.filter((row) => row.status?.first && row.status?.second && row.status.first !== row.status.second);
    } else if (statusFilter !== "all") {
      rows = rows.filter((row) => (row.status?.second || "").toUpperCase() === statusFilter);
    }
    if (serviceFilter !== "all") {
      rows = rows.filter((row) => resolveCheckServices(row).includes(serviceFilter));
    }
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter((row) => (row.title || row.checkId || "").toLowerCase().includes(q));
    }
    return rows;
  }, [changedChecks, severityFilter, statusFilter, serviceFilter, search, servicesByCheckId]);

  useEffect(() => {
    setPage(0);
  }, [severityFilter, statusFilter, serviceFilter, search, cspmCompareResult]);

  const paginatedRows = filteredChecks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const openResourceCompare = (row, tab = null) => {
    const firstUri = row?.s3Uri?.first;
    const secondUri = row?.s3Uri?.second;
    if (!firstUri || !secondUri) return;
    setSelectedCheck(row);
    setSelectedResourceTab(tab);
    setDrawerOpen(true);
    dispatch(
      compareCspmResources({
        first_run_uri: firstUri,
        second_run_uri: secondUri,
      })
    );
  };

  const retryResourceCompare = () => {
    if (!selectedCheck?.s3Uri?.first || !selectedCheck?.s3Uri?.second) return;
    dispatch(
      compareCspmResources({
        first_run_uri: selectedCheck.s3Uri.first,
        second_run_uri: selectedCheck.s3Uri.second,
      })
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          mb: 2,
          p: { xs: 1.5, sm: 2 },
          flexWrap: "wrap",
          flexShrink: 0,
          ...CONFIG_CARD_SX,
          ...ACCOUNT_DETAIL_FONT_SX,
        }}
      >
        {accountsWithWorkloads.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 220, maxWidth: 280, flex: "0 1 260px" }}>
            <Select
              value={selectValue}
              onChange={handleAccountDropdownChange}
              displayEmpty
              aria-label="Account or workload"
              renderValue={() => (
                <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                  <CloudProviderMark provider={selectedProvider} sx={{ mr: 0.75 }} />
                  <Box
                    component="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#111827",
                    }}
                  >
                    {selectedDisplay}
                  </Box>
                </Box>
              )}
              sx={accountSelectSx}
              MenuProps={selectMenuProps}
            >
              {accountsWithWorkloads.flatMap((acc) => [
                <MenuItem key={acc.accountNumber} value={acc.accountNumber} sx={MENU_ITEM_SX}>
                  {acc.accountName} ({acc.accountNumber})
                </MenuItem>,
                ...(acc.schedules || []).map((item) => (
                  <MenuItem key={item.scheduleId} value={item.scheduleId} sx={{ ...MENU_ITEM_SX, pl: 3.5 }}>
                    {item.scheduleName}
                  </MenuItem>
                )),
              ])}
            </Select>
          </FormControl>
        )}

        <Box
          component="button"
          type="button"
          onClick={() => setPickerOpen(true)}
          aria-label="Select scans to compare"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            flex: "0 0 auto",
            width: "fit-content",
            maxWidth: "100%",
            height: FIELD_HEIGHT,
            px: 1.25,
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            backgroundColor: "#fff",
            cursor: "pointer",
            textAlign: "left",
            fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
            appearance: "none",
            "&:hover": {
              borderColor: "#cbd5e1",
              backgroundColor: "#f8fafc",
            },
          }}
        >
          <CalendarMonthIcon sx={{ color: "#94a3b8", fontSize: 18, flexShrink: 0 }} />
          <ScanSlot
            label="Baseline"
            value={baselineScan?.scanDate ? formatUtcDateSimple(baselineScan.scanDate) : ""}
          />
          <ArrowForwardIcon sx={{ fontSize: 16, color: "#cbd5e1", flexShrink: 0 }} />
          <ScanSlot
            label="Compare"
            value={compareScan?.scanDate ? formatUtcDateSimple(compareScan.scanDate) : ""}
          />
          <KeyboardArrowDownIcon sx={{ color: "#94a3b8", fontSize: 20, flexShrink: 0 }} />
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
      {isGettingCspmChecks && !pickerOpen ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress color="primary" />
        </Box>
      ) : isComparingCspmScans ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress color="primary" />
        </Box>
      ) : compareCspmScansError ? (
        <ErrorUI error={compareCspmScansError} onRetry={handleCompare} />
      ) : !cspmCompareResult ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            textAlign: "center",
            ...CONFIG_CARD_SX,
            ...ACCOUNT_DETAIL_FONT_SX,
          }}
        >
          <Typography
            sx={{
              mb: 0.5,
              fontWeight: 700,
              fontSize: "1rem",
              color: "#111827",
            }}
          >
            Select two scans to compare
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 400, color: "#64748b" }}>
            Choose Baseline and Compare scans in the picker, then click Compare Scans.
          </Typography>
        </Paper>
      ) : (
        <>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2.5 },
              ...CONFIG_CARD_SX,
              ...ACCOUNT_DETAIL_FONT_SX,
            }}
          >
            <CompareSummaryCards
              result={cspmCompareResult}
              baselineDate={baselineScan?.scanDate}
              compareDate={compareScan?.scanDate}
            />

            <Box
              sx={{
                pb: 1.25,
                mb: 1.75,
                borderBottom: "1px solid #e5e7eb",
                flexShrink: 0,
              }}
            >
              <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "1rem" }}>
                Changed checks: {cspmCompareResult.totalChangedChecks ?? changedChecks.length}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "stretch", sm: "center" },
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                mb: 2,
                flexWrap: "wrap",
                flexShrink: 0,
              }}
            >
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                size="small"
                sx={FILTER_SELECT_SX}
                MenuProps={selectMenuProps}
                renderValue={(val) =>
                  val === "all" ? "All" : val === "changed" ? "Status changed" : toTitleCaseLabel(val)
                }
              >
                <MenuItem value="all" sx={MENU_ITEM_SX}>All</MenuItem>
                <MenuItem value="changed" sx={MENU_ITEM_SX}>Status changed</MenuItem>
                <MenuItem value="FAILED" sx={MENU_ITEM_SX}>Failed</MenuItem>
                <MenuItem value="PASSED" sx={MENU_ITEM_SX}>Passed</MenuItem>
                <MenuItem value="MANUAL" sx={MENU_ITEM_SX}>Manual</MenuItem>
              </Select>
              <Select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                size="small"
                sx={{ ...FILTER_SELECT_SX, minWidth: 120 }}
                MenuProps={selectMenuProps}
                renderValue={(val) => (val === "all" ? "All" : toTitleCaseLabel(val))}
              >
                <MenuItem value="all" sx={MENU_ITEM_SX}>All</MenuItem>
                <MenuItem value="CRITICAL" sx={MENU_ITEM_SX}>Critical</MenuItem>
                <MenuItem value="HIGH" sx={MENU_ITEM_SX}>High</MenuItem>
                <MenuItem value="MEDIUM" sx={MENU_ITEM_SX}>Medium</MenuItem>
                <MenuItem value="LOW" sx={MENU_ITEM_SX}>Low</MenuItem>
                <MenuItem value="INFORMATIONAL" sx={MENU_ITEM_SX}>Informational</MenuItem>
              </Select>
              <Select
                value={serviceFilter}
                onChange={(e) => {
                  const next = e.target.value;
                  setServiceFilter(next);
                  if (
                    baselineScan?.runId &&
                    compareScan?.runId &&
                    baselineScan.runId !== compareScan.runId
                  ) {
                    dispatch(
                      compareCspmScans(
                        compareScansPayload(baselineScan.runId, compareScan.runId, next)
                      )
                    );
                  }
                }}
                size="small"
                sx={FILTER_SELECT_SX}
                MenuProps={selectMenuProps}
                renderValue={(val) => (val === "all" ? "All" : val)}
              >
                <MenuItem value="all" sx={MENU_ITEM_SX}>All</MenuItem>
                {availableServices.map((service) => (
                  <MenuItem key={service} value={service} sx={MENU_ITEM_SX}>
                    {service}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                size="small"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                    </InputAdornment>
                  ),
                }}
                sx={SEARCH_FIELD_SX}
              />
            </Box>

            <Box
              sx={{
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                backgroundColor: "#fff",
                overflow: "hidden",
              }}
            >
              <TableContainer sx={{ overflowX: "hidden" }}>
                <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        rowSpan={2}
                        align="center"
                        sx={{
                          ...GROUP_HEADER_SX,
                          ...TITLE_COL_WIDTH,
                          verticalAlign: "middle",
                          ...GROUP_END_SX,
                        }}
                      >
                        Title
                      </TableCell>
                      <ScanGroupHeaderCell
                        label="Baseline"
                        date={baselineScan?.scanDate ? formatUtcDateSimple(baselineScan.scanDate) : ""}
                      />
                      <ScanGroupHeaderCell
                        label="Compare"
                        date={compareScan?.scanDate ? formatUtcDateSimple(compareScan.scanDate) : ""}
                      />
                      <TableCell
                        rowSpan={2}
                        align="center"
                        sx={{
                          ...GROUP_HEADER_SX,
                          verticalAlign: "middle",
                          width: "22%",
                        }}
                      >
                        Resources
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      {["Severity", "Status", "Pass rate", "Severity", "Status", "Pass rate"].map(
                        (label, index) => {
                          const info = COMPARE_COLUMN_INFO[index];
                          return (
                            <TableCell
                              key={`${label}-${index}`}
                              align="center"
                              sx={{
                                ...HEADER_CELL_SX,
                                px: 0.75,
                                width: "8%",
                                whiteSpace: "nowrap",
                                lineHeight: 1.25,
                                ...((index === 2 || index === 5) ? GROUP_END_SX : null),
                              }}
                            >
                              {info ? (
                                <Box
                                  component="span"
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0.25,
                                  }}
                                >
                                  {label}
                                  <Tooltip title={info} arrow placement="top">
                                    <InfoOutlinedIcon
                                      sx={{
                                      fontSize: 14,
                                      color: "#94a3b8",
                                      "&:hover": { color: "#64748b" },
                                      }}
                                    />
                                  </Tooltip>
                                </Box>
                              ) : (
                                label
                              )}
                            </TableCell>
                          );
                        }
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={COMPARE_COLUMN_COUNT} align="center" sx={{ ...BODY_CELL_SX, py: 5 }}>
                          <Typography sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                            No checks changed between these scans.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedRows.map((row, idx) => {
                        const rowKey = row.checkId || idx;
                        const firstSeverity = (row.severity?.first || "").toUpperCase();
                        const secondSeverity = (row.severity?.second || "").toUpperCase();
                        const firstStatus = row.status?.first;
                        const secondStatus = row.status?.second;
                        const passRate = row.changes?.PassRate;
                        const canOpenResources = Boolean(row.s3Uri?.first && row.s3Uri?.second);
                        return (
                          <React.Fragment key={rowKey}>
                            <TableRow
                              hover
                              selected={expandedRow === rowKey}
                              onClick={() =>
                                setExpandedRow(expandedRow === rowKey ? null : rowKey)
                              }
                              sx={{
                                cursor: "pointer",
                                "&:hover": { backgroundColor: "#f8fafc" },
                                "&.Mui-selected": { backgroundColor: "#f8fafc" },
                                "&.Mui-selected:hover": { backgroundColor: "#f1f5f9" },
                              }}
                            >
                              <TableCell align="left" sx={{ ...BODY_CELL_SX, ...TITLE_COL_WIDTH, ...GROUP_END_SX }}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                                  <IconButton
                                    size="small"
                                    tabIndex={-1}
                                    sx={{
                                      mt: "-2px",
                                      color: "#94a3b8",
                                      pointerEvents: "none",
                                      flexShrink: 0,
                                    }}
                                  >
                                    {expandedRow === rowKey ? (
                                      <KeyboardArrowDownIcon fontSize="small" />
                                    ) : (
                                      <KeyboardArrowRightIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                  <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                                    <Typography
                                      sx={{
                                        fontSize: "0.8125rem",
                                        fontWeight: 500,
                                        color: "#111827",
                                        lineHeight: 1.4,
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {row.title}
                                    </Typography>
                                    {row.changeType && (
                                      <Chip size="small" label={row.changeType} sx={{ mt: 0.75, ...CHANGE_TYPE_CHIP_SX }} />
                                    )}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell align="center" sx={BODY_CELL_SX}>
                                <Typography
                                  sx={{
                                    color: getSeverityColor(firstSeverity),
                                    fontWeight: 600,
                                    fontSize: "0.8125rem",
                                  }}
                                >
                                  {firstSeverity || "—"}
                                </Typography>
                              </TableCell>
                              <TableCell align="center" sx={BODY_CELL_SX}>
                                <Typography
                                  sx={{
                                    color: getStatusColor(firstStatus),
                                    fontWeight: 600,
                                    fontSize: "0.8125rem",
                                  }}
                                >
                                  {firstStatus || "—"}
                                </Typography>
                              </TableCell>
                              <TableCell align="center" sx={{ ...BODY_CELL_SX, ...GROUP_END_SX }}>
                                <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#111827" }}>
                                  {formatScore(passRate?.first)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center" sx={BODY_CELL_SX}>
                                <Typography
                                  sx={{
                                    color: getSeverityColor(secondSeverity),
                                    fontWeight: 600,
                                    fontSize: "0.8125rem",
                                  }}
                                >
                                  {secondSeverity || "—"}
                                </Typography>
                              </TableCell>
                              <TableCell align="center" sx={BODY_CELL_SX}>
                                <Typography
                                  sx={{
                                    color: getStatusColor(secondStatus),
                                    fontWeight: 600,
                                    fontSize: "0.8125rem",
                                  }}
                                >
                                  {secondStatus || "—"}
                                </Typography>
                              </TableCell>
                              <TableCell align="center" sx={{ ...BODY_CELL_SX, ...GROUP_END_SX }}>
                                <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#111827" }}>
                                  {formatScore(passRate?.second)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center" sx={{ ...BODY_CELL_SX, width: "22%", px: 1 }}>
                                {canOpenResources ? (
                                  <Tooltip title="Open the resource comparison for this check." arrow placement="top">
                                    <Chip
                                      label="View resource comparison"
                                      size="small"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        openResourceCompare(row);
                                      }}
                                      sx={{
                                        height: 26,
                                        maxWidth: "100%",
                                        borderRadius: "6px",
                                        backgroundColor: ACCENT,
                                        color: "#fff",
                                        fontWeight: 600,
                                        fontSize: "0.75rem",
                                        cursor: "pointer",
                                        "& .MuiChip-label": { px: 1.25 },
                                        "&:hover": { backgroundColor: "#e8741c" },
                                      }}
                                    />
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="No resources are available to compare for this check." arrow placement="top">
                                    <Chip
                                      label="No resources"
                                      size="small"
                                      sx={{
                                        height: 26,
                                        borderRadius: "6px",
                                        backgroundColor: "#f1f5f9",
                                        color: "#64748b",
                                        fontWeight: 600,
                                        fontSize: "0.75rem",
                                        cursor: "default",
                                        "& .MuiChip-label": { px: 1.25 },
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                            {expandedRow === rowKey && (
                              <TableRow>
                                <TableCell
                                  colSpan={COMPARE_COLUMN_COUNT}
                                  sx={{
                                    backgroundColor: "#f8fafc",
                                    borderBottom: "1px solid #f1f5f9",
                                    px: 1.75,
                                    py: 1.25,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      flexWrap: "wrap",
                                      gap: 1.5,
                                      pl: 4.5,
                                    }}
                                  >
                                    <Typography sx={{ fontSize: "0.8125rem", color: "#334155" }}>
                                      Check ID: {row.checkId}
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <EditAccountTablePagination
                page={page}
                rowsPerPage={rowsPerPage}
                total={filteredChecks.length}
                rowsPerPageOptions={[10, 20, 30]}
                onPageChange={setPage}
                onRowsPerPageChange={(value) => {
                  setRowsPerPage(value);
                  setPage(0);
                }}
              />
            </Box>
          </Paper>
        </>
      )}
      </Box>

      <CompareResourcesDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedResourceTab(null);
        }}
        checkTitle={selectedCheck?.title || ""}
        onRetry={retryResourceCompare}
        initialTab={selectedResourceTab}
      />
      <CompareScanPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        selectedAccount={selectedAccount}
        scans={Object.values(cspmResultOrderedMap || {}).filter((scan) => scan && scan.scanDate)}
        availableDates={availableDates}
        workloadScheduleName={selectedWorkloadName}
        isLoading={isGettingCspmChecks}
        initialFirst={baselineScan}
        initialSecond={compareScan}
        onConfirmPair={applyScanPair}
      />
    </Box>
  );
};

export default ComparativeScan;
